import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useProjectContext } from '../../context/ProjectContext'
import { fabricTypes } from '../../data/fabricTypes'

const ProjectSetup = () => {
  const { project, updateProject, initializeGrid } = useProjectContext()

  const [showRebuildWarning, setShowRebuildWarning] = useState(false)
  const lastAppliedRef = useRef({
    
    width: Number(project.width),
    height: Number(project.height),
    unit: project.unit || 'in',
    stitchesPerInch: Number(project.stitchesPerInch),
  })

  const ALLOWED_SPI = [4, 6, 8, 10, 12]

  const normalizeSpi = (value) => {
    const num = Math.round(Number(value) || 8)

    return ALLOWED_SPI.reduce((closest, current) => {
      return Math.abs(current - num) < Math.abs(closest - num)
        ? current
        : closest
    }, ALLOWED_SPI[0])
  }

  // Detect if there is painted work on the grid
  const hasPaintedWork = useMemo(() => {
    const g = project.grid || []
    for (let r = 0; r < g.length; r++) {
      for (let c = 0; c < (g[r] || []).length; c++) {
        if (g[r][c]?.colorId) return true
      }
    }
    return false
  }, [project.grid])

  // Consider "settings that would erase work" as dirty vs last applied
  const settingsChangedSinceApply = useMemo(() => {
    const last = lastAppliedRef.current

    const w = Number(project.width)
    const h = Number(project.height)
    const spi = Number(project.stitchesPerInch)
    const unit = project.unit || 'in'

    return (
      w !== Number(last.width) ||
      h !== Number(last.height) ||
      unit !== (last.unit || 'in') ||
      spi !== Number(last.stitchesPerInch)
    )
  }, [project.width, project.height, project.unit, project.stitchesPerInch])

useEffect(() => {
  if (!settingsChangedSinceApply) {
    setShowRebuildWarning(false)
    return
  }

  const timeoutId = setTimeout(() => {
    initializeGrid()
    lastAppliedRef.current = {
      width: Number(project.width),
      height: Number(project.height),
      unit: project.unit || 'in',
      stitchesPerInch: Number(project.stitchesPerInch),
    }
    setShowRebuildWarning(false)
  }, 350)

  return () => clearTimeout(timeoutId)
}, [
  project.width,
  project.height,
  project.unit,
  project.stitchesPerInch,
  settingsChangedSinceApply,
  initializeGrid,
])

  const handleInputChange = (field, value) => {
    updateProject({ [field]: value })
  }

  const handleFabricChange = (fabricId) => {
    const fabric = fabricTypes.find((f) => f.id === fabricId)
    if (fabric) {
      updateProject({
        fabricType: fabricId,
        stitchesPerInch: fabric.defaultSPI,
      })
    }
  }

  const handleUnitChange = (newUnit) => {
    const oldUnit = project.unit || 'in'
    if (newUnit === oldUnit) return

    const convert = (val, from, to) => {
      if (from === to) return val
      return from === 'in' && to === 'cm' ? val * 2.54 : val / 2.54
    }

    updateProject({
      unit: newUnit,
      width: Number(convert(project.width, oldUnit, newUnit).toFixed(2)),
      height: Number(convert(project.height, oldUnit, newUnit).toFixed(2)),
    })
  }

  const handleRebuildErase = () => {
    initializeGrid()
    
    lastAppliedRef.current = {
      width: Number(project.width),
      height: Number(project.height),
      unit: project.unit || 'in',
      stitchesPerInch: Number(project.stitchesPerInch),
    }
    setShowRebuildWarning(false)
  }

const handleCancelRebuild = () => {
  const last = lastAppliedRef.current

  updateProject({
    width: last.width,
    height: last.height,
    unit: last.unit,
    stitchesPerInch: last.stitchesPerInch,
  })

  setShowRebuildWarning(false)
}

  // Grid size preview (always computed in inches internally)
  const gridCols = Math.ceil(
    (project.unit === 'cm' ? Number(project.width) / 2.54 : Number(project.width)) *
      Number(project.stitchesPerInch)
  )
  const gridRows = Math.ceil(
    (project.unit === 'cm' ? Number(project.height) / 2.54 : Number(project.height)) *
      Number(project.stitchesPerInch)
  )

  return (
    <div className="project-setup">
      <h2>Project Setup</h2>

      {showRebuildWarning && (
        <div className="rebuild-warning rebuild-warning--sticky" role="alert" aria-live="assertive">
          <div className="rebuild-warning__title">⚠️ CHANGING SETTINGS WILL ERASE YOUR DESIGN</div>
          <div className="rebuild-warning__actions">
            <button className="rebuild-warning__danger" onClick={handleRebuildErase}>
              Rebuild (Erase)
            </button>
            <button className="rebuild-warning__cancel" onClick={handleCancelRebuild}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dim + block clicks when warning is shown */}
      <div className={showRebuildWarning ? 'rebuild-dimmed' : ''}>
        <div className="form-group">
          <label htmlFor="project-name">Project Name</label>
          <input
            id="project-name"
            type="text"
            value={project.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </div>

        {/* Units + Fabric */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="unit">Units</label>
            <select id="unit" value={project.unit || 'in'} onChange={(e) => handleUnitChange(e.target.value)}>
              <option value="in">Inches</option>
              <option value="cm">Centimeters</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="fabric-type">Fabric Type</label>
            <select
              id="fabric-type"
              value={project.fabricType}
              onChange={(e) => handleFabricChange(e.target.value)}
            >
              {fabricTypes.map((fabric) => (
                <option key={fabric.id} value={fabric.id}>
                  {fabric.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Width + Height */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="width">Width ({project.unit === 'cm' ? 'cm' : 'in'})</label>
            <input
              id="width"
              type="number"
              min="1"
              max="100"
              value={project.width}
              onChange={(e) => handleInputChange('width', parseFloat(e.target.value))}
            />
          </div>

          <div className="form-group">
            <label htmlFor="height">Height ({project.unit === 'cm' ? 'cm' : 'in'})</label>
            <input
              id="height"
              type="number"
              min="1"
              max="100"
              value={project.height}
              onChange={(e) => handleInputChange('height', parseFloat(e.target.value))}
            />
          </div>
        </div>

        {/* SPI + Grid */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="spi">
              Stitches per inch (SPI)
              {project.unit === 'cm' ? ' — cm converts to inches' : ''}
            </label>
            
            <input
              id="spi"
              type="number"
              min="4"
              max="12"
              step="2"
              value={project.stitchesPerInch}
              onChange={(e) =>
                handleInputChange(
                  'stitchesPerInch',
                  normalizeSpi(e.target.value)
                 )
              }
            />
          </div>

          <div className="form-group">
            <label>Grid Size</label>
            <div className="grid-size-box">
              {gridCols} × {gridRows} cells
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectSetup